import { type FC, useCallback, useRef, useState } from 'react'
import classNames from 'classnames'
import { Container } from 'react-bootstrap'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { Permission } from '../../contexts/permission/PermissionModel'
import { useAppSelector } from '../../store/hooks'
import { useInsightsData, type TimeRangeKey, TIME_RANGE_TO_DAYS } from './useInsightsData'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { fmt } from './insightsUtils'
import { InsightsSection, EmptyState } from './insightsCommon'
import { HighlightsSection } from './HighlightsSection'
import { DiscussionTopicsSection } from './DiscussionTopicsSection'
import { ClientOutlookSection } from './ClientOutlookSection'
import { AssetOpportunitiesSection } from './AssetOpportunitiesSection'
import { ReferralsSection } from './ReferralsSection'
import { AdvisorFilter } from './AdvisorFilter'
import pluralize from 'pluralize'
import { useScrollLock } from '../../hooks/useScrollLock'
import './InsightsView.scss'
import { InsightsEmptyState } from './InsightsEmptyState/InsightsEmptyState'
import { InsightsFeedback } from './Feedback/InsightsFeedback'
import { useInsightsFeedback } from './hooks/useInsightsFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'

const TIME_RANGES = Object.keys(TIME_RANGE_TO_DAYS) as TimeRangeKey[]

function formatDateRange(timeRange: TimeRangeKey): string {
  const now = new Date()
  const start = new Date(now)
  start.setDate(now.getDate() - TIME_RANGE_TO_DAYS[timeRange])
  const dateFormatter = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' })
  return `${dateFormatter.format(start)} \u2013 ${dateFormatter.format(now)}`
}

export const InsightsView: FC = () => {
  useDocumentTitle('Insights')
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const hasDashboardAdmin = currentUser?.permissions?.includes(Permission.DASHBOARD_ADMIN.id) ?? false
  const { hasQuota, isFetching: quotasIsFetching } = useZocksQuota()
  const dashboardAvailable = hasQuota('insights.enabled.value')
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('30D')
  const [selectedAdvisorIds, setSelectedAdvisorIds] = useState<string[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const { navigateTo, shouldShowInsightsFeedbackCTA, handleDismiss, handleFeedbackClick } = useInsightsFeedback()
  const { trackingClient } = useTracking()

  const handleAdvisorFilterChange = useCallback(
    (ids: string[]) => {
      setSelectedAdvisorIds(ids)
      trackingClient.track({
        eventName: TrackEvent.INSIGHTS_ADVISOR_FILTER_CHANGED,
        eventDetails: { advisorCount: ids.length },
      })
    },
    [trackingClient],
  )

  const { data, isLoading, isFetching, isError } = useInsightsData(accountId ?? undefined, timeRange, {
    enabled: !!dashboardAvailable && !!accountId,
    userIdList: selectedAdvisorIds.length > 0 ? selectedAdvisorIds : undefined,
  })

  const displayData = data
  const isInitialLoading = dashboardAvailable && isLoading && !displayData
  const isRefetching = isFetching && !isLoading && !!displayData
  const accountDashboardAvailable = () => !quotasIsFetching && dashboardAvailable
  const showEmptyState = displayData?.highlights.meetingCount === 0

  useScrollLock(containerRef, isRefetching)

  const getInsightsSubtitle = (meetingCount: number, advisorsCount: number) => {
    const meetings = `Based on ${fmt(meetingCount)} ${pluralize('meeting', meetingCount)}`
    const advisors = advisorsCount > 0 ? ` across ${advisorsCount} ${pluralize('advisor', advisorsCount)}` : ''
    return `${meetings}${advisors} captured ${formatDateRange(timeRange)}`
  }

  return (
    <Container fluid ref={containerRef} className='d-flex flex-column zocks-gap-1.25 p-0 w-100 full-size-content insights-view'>
      {!quotasIsFetching && (isInitialLoading || dashboardAvailable) && (
        <ScopedBlock authActions={[accountDashboardAvailable]} missingScopeAction={MissingScopeAction.HIDE}>
          <div className='d-flex justify-content-between align-items-start'>
            <div>
              <h1 className='h1 fw-bolder'>Insights</h1>
              {displayData && <p className='text-muted mb-0 small'>{getInsightsSubtitle(displayData.highlights.meetingCount, selectedAdvisorIds.length)}</p>}
            </div>
            <div className='d-flex align-items-center gap-3'>
              {hasDashboardAdmin && accountId && (
                <AdvisorFilter
                  accountId={accountId}
                  selectedUserIds={selectedAdvisorIds}
                  onSelectionChange={handleAdvisorFilterChange}
                  disabled={isInitialLoading || isRefetching}
                />
              )}
              <div className='insights-time-range-selector' role='group'>
                {TIME_RANGES.map((range) => (
                  <button
                    key={range}
                    type='button'
                    className={classNames('insights-time-range-btn', { 'insights-time-range-btn--active': timeRange === range })}
                    onClick={() => {
                      setTimeRange(range)
                      trackingClient.track({
                        eventName: TrackEvent.INSIGHTS_TIME_RANGE_CHANGED,
                        eventDetails: { timeRange: range },
                      })
                    }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isInitialLoading && (
            <div className='insights-initial-loading'>
              <div className='insights-loading-spinner' />
              <div className='insights-loading-text'>Pulling up insights...</div>
            </div>
          )}

          {isError && !displayData && (
            <div className='insights-initial-loading'>
              <EmptyState
                icon={<i className='bi bi-exclamation-triangle' />}
                title='Something went wrong'
                subtitle="We couldn't load your insights. Please try again later."
              />
            </div>
          )}

          {isRefetching && (
            <div className='insights-filter-loading-overlay'>
              <div className='insights-filter-loading-overlay__content'>
                <div className='insights-loading-spinner' />
                <div className='insights-loading-text insights-loading-text--light'>Pulling up insights...</div>
              </div>
            </div>
          )}

          {displayData && !showEmptyState && (
            <div className='position-relative sections-container'>
              <HighlightsSection highlights={displayData.highlights} timeRange={timeRange} />

              <h2 className='insights-explore-title mt-3 mb-3'>Explore the data behind the highlights</h2>
              <DiscussionTopicsSection topics={displayData.discussionTopics} products={displayData.investmentProducts} />

              {shouldShowInsightsFeedbackCTA && <InsightsFeedback to={navigateTo} onDismiss={handleDismiss} onFeedbackClick={handleFeedbackClick} />}

              {displayData.clientOutlook && <ClientOutlookSection data={displayData.clientOutlook} />}

              {displayData.assetOpportunities && <AssetOpportunitiesSection data={displayData.assetOpportunities} />}

              {displayData.referrals && <ReferralsSection data={displayData.referrals} />}

              {!displayData.clientOutlook && (
                <InsightsSection title='Client outlook' subtitle='Clients expressed sentiment about specific asset classes'>
                  <EmptyState icon={<i className='bi bi-binoculars' />} title='No sentiment detected' subtitle='Try selecting a wider time range' />
                </InsightsSection>
              )}

              {!displayData.assetOpportunities && (
                <InsightsSection title='Asset opportunities' subtitle='Track how held-away assets move from mention to client agreement'>
                  <EmptyState icon={<i className='bi bi-bullseye' />} title='No asset mentions' subtitle='Try selecting a wider time range' />
                </InsightsSection>
              )}

              {!displayData.referrals && (
                <InsightsSection title='Referrals' subtitle='See where referrals are coming from and where to ask more'>
                  <EmptyState icon={<i className='bi bi-people' />} title='No referral activity' subtitle='Try selecting a wider time range' />
                </InsightsSection>
              )}
            </div>
          )}

          {showEmptyState && <InsightsEmptyState />}
        </ScopedBlock>
      )}
      {!quotasIsFetching && !dashboardAvailable && (
        <div className='d-flex flex-grow-1 justify-content-center align-items-center'>
          <Upsell feature={DELTA_TRIAL_FEATURES['insights']} />
        </div>
      )}
    </Container>
  )
}
